import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Alert,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import tw from 'twrnc';

import { isScreenshotMode } from '../../../../lib/screenshotMode';
import { ENV_CONFIG } from '../../../config/environment';
import type { RootStackParamList } from '../../../navigation/types';
import { useAuth } from '../../../shared/hooks/useAuth';
import { getSubscriptionPlans } from '../config/subscriptionPlans';
import { entitlementService } from '../services/entitlementService';
import { revenueCatService } from '../services/revenueCatService';
import { subscriptionStateManager } from '../services/subscriptionStateManager';
import { gateText, uiText } from '../../../shared/utils/uiText';
import {
  PremiumFeature,
  FEATURE_DESCRIPTIONS,
  getMinimumTierForFeature,
  SubscriptionTier,
  TIER_FEATURES,
} from '../types/premiumFeatures';

interface PremiumFeatureGateProps {
  feature: PremiumFeature | string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
  customMessage?: string;
  fallbackMessage?: string;
  upgradeButtonText?: string;
  onAccessGranted?: () => void;
  onAccessDenied?: () => void;
}

const normalizePremiumFeature = (feature: PremiumFeature | string): PremiumFeature | null => {
  return Object.values(PremiumFeature).includes(feature as PremiumFeature)
    ? (feature as PremiumFeature)
    : null;
};

const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

const resolveFeatureAccess = async (
  userId: string,
  feature: PremiumFeature | string,
  normalizedFeature: PremiumFeature | null,
): Promise<boolean> => {
  if (isNativeMobile && normalizedFeature) {
    const revenueCatSubscription = await revenueCatService.getSubscriptionInfo();
    if (
      revenueCatSubscription?.isActive &&
      TIER_FEATURES[SubscriptionTier.PREMIUM].includes(normalizedFeature)
    ) {
      return true;
    }
  }

  return normalizedFeature
    ? entitlementService.hasFeatureAccess(userId, normalizedFeature)
    : false;
};

// Cache for feature access to prevent flashing
const featureAccessCache = new Map<string, { access: boolean; timestamp: number }>();
const CACHE_TTL = 60000; // 60 seconds cache (1 minute)

// Export function to clear cache when subscription changes
export const clearFeatureAccessCache = (userId?: string) => {
  if (userId) {
    // Clear all entries for specific user
    const keysToDelete: string[] = [];
    featureAccessCache.forEach((_, key) => {
      if (key.startsWith(`${userId}-`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => featureAccessCache.delete(key));
  } else {
    // Clear entire cache
    featureAccessCache.clear();
  }
};

// Export function to pre-warm cache for common features
export const preWarmFeatureCache = async (userId: string, features: PremiumFeature[]) => {
  if (!userId) return;

  try {
    // Check multiple features in parallel to warm the cache
    const checks = features.map(async (feature) => {
      try {
        const access = await resolveFeatureAccess(userId, feature, feature);
        const cacheKey = `${userId}-${feature}`;
        featureAccessCache.set(cacheKey, { access, timestamp: Date.now() });
        return { feature, access };
      } catch (error) {
        console.error(`Failed to pre-warm cache for feature ${feature}:`, error);
        return { feature, access: false };
      }
    });

    await Promise.all(checks);
    console.log(`Pre-warmed feature cache for ${features.length} features`);
  } catch (error) {
    console.error('Failed to pre-warm feature cache:', error);
  }
};

export const PremiumFeatureGate: React.FC<PremiumFeatureGateProps> = ({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
  customMessage,
  fallbackMessage,
  upgradeButtonText,
  onAccessGranted,
  onAccessDenied
}) => {
  // Store screenshot capture: premium gates would render upsell cards over the
  // content being photographed — treat every feature as available.
  if (isScreenshotMode()) {
    return <>{children}</>;
  }

  const normalizedFeature = normalizePremiumFeature(feature);
  // Check cache first to prevent flashing
  const getCachedAccess = (userId: string, featureKey: string) => {
    const cacheKey = `${userId}-${featureKey}`;
    const cached = featureAccessCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.access;
    }

    return null;
  };

  const setCachedAccess = (userId: string, featureKey: string, access: boolean) => {
    const cacheKey = `${userId}-${featureKey}`;
    featureAccessCache.set(cacheKey, { access, timestamp: Date.now() });
  };

  const { user } = useAuth();
  const isFocused = useIsFocused();

  // Initialize with cached value if available
  const initialAccess = user?.id ? getCachedAccess(user.id, feature) : null;
  const [hasAccess, setHasAccess] = useState<boolean | null>(initialAccess);
  const [loading, setLoading] = useState(initialAccess === null);

  useEffect(() => {
    checkFeatureAccess();

    // Subscribe to subscription state changes for immediate updates
    const unsubscribe = subscriptionStateManager.subscribe(() => {
      console.log(`🔄 [PremiumFeatureGate] Subscription state changed, rechecking feature: ${feature}`);
      checkFeatureAccess();
    });

    return unsubscribe;
  }, [user, feature]);

  // Re-check when screen comes into focus (bypass cache)
  useEffect(() => {
    if (isFocused && user?.id) {
      // Clear cache for this feature so we get a fresh check
      const cacheKey = `${user.id}-${feature}`;
      featureAccessCache.delete(cacheKey);
      checkFeatureAccess();
    }
  }, [isFocused]);

  const checkFeatureAccess = async () => {
    if (!user?.id) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    // If we have cached value, use it but still refresh in background
    const cachedValue = getCachedAccess(user.id, feature);
    if (cachedValue !== null) {
      setHasAccess(cachedValue);
      setLoading(false);

      // Still check in background to refresh cache
      try {
        const access = await resolveFeatureAccess(user.id, feature, normalizedFeature);
        setCachedAccess(user.id, feature, access);

        // Only update state if different from cached
        if (access !== cachedValue) {
          setHasAccess(access);
        }

        if (access) {
          onAccessGranted?.();
        } else {
          onAccessDenied?.();
        }
      } catch (error) {
        console.error('Failed to refresh feature access:', error);
      }
      return;
    }

    // No cached value, need to check
    try {
      const access = await resolveFeatureAccess(user.id, feature, normalizedFeature);
      setCachedAccess(user.id, feature, access);
      setHasAccess(access);

      if (access) {
        onAccessGranted?.();
      } else {
        onAccessDenied?.();
      }
    } catch (error) {
      console.error('Failed to check feature access:', error);
      // For subscribed users, default to allowing access on error to prevent upgrade modal flashing
      // The entitlementService logs errors for debugging
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  // While loading for the first time, show loading state instead of optimistically rendering
  // This prevents the premium modal from showing for subscribed users
  if (loading) {
    return (
      <View style={tw`p-4 items-center justify-center`}>
        <ActivityIndicator size="small" color="#4F46E5" />
      </View>
    );
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If no access and no upgrade prompt, render fallback or nothing
  if (!showUpgradePrompt) {
    return <>{fallback || null}</>;
  }

  // Render upgrade prompt
  return (
    <UpgradePrompt
      feature={feature}
      customMessage={customMessage ?? fallbackMessage}
      upgradeButtonText={upgradeButtonText}
      fallback={fallback}
    />
  );
};

// Upgrade Prompt Component
interface UpgradePromptProps {
  feature: PremiumFeature | string;
  customMessage?: string;
  upgradeButtonText?: string;
  fallback?: React.ReactNode;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ 
  feature, 
  customMessage,
  upgradeButtonText,
  fallback 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { user } = useAuth();
  const normalizedFeature = normalizePremiumFeature(feature);

  const isPreviewMode = !ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED;

  // Handle database feature keys that aren't in TypeScript enum
  const getFeatureDescription = (featureKey: string) => {
    if (featureKey === 'live_events_host') {
      return 'Live hosted events now belong to the separate church and large-group experience.';
    }
    // Only use FEATURE_DESCRIPTIONS for actual PremiumFeature enum values
    if (Object.values(PremiumFeature).includes(featureKey as PremiumFeature)) {
      return FEATURE_DESCRIPTIONS[featureKey as PremiumFeature];
    }
    return featureKey;
  };

  const featureDescription = gateText(getFeatureDescription(feature));
  const minimumTier = normalizedFeature ? getMinimumTierForFeature(normalizedFeature) : null;
  const recommendedPlan = minimumTier ? getSubscriptionPlans().find(p => p.tier === minimumTier) : null;

  const handleUpgradePress = () => {
    if (!user) {
      Alert.alert(uiText('signInRequired'), uiText('signInToAccessPremium'));
      return;
    }

    if (isPreviewMode) {
      setShowModal(true);
    } else {
      // Navigate to subscription plans screen
      navigation.navigate('SubscriptionPlans', {
        preselectedPlan: recommendedPlan?.id
      });
    }
  };

  const handleLearnMore = () => {
    setShowModal(true);
  };

  if (dismissed) {
    if (fallback) return <>{fallback}</>;
    // No fallback — navigate back instead of showing blank screen
    try { navigation.goBack(); } catch { /* silent */ }
    return null;
  }

  return (
    <View style={[tw`bg-white p-6 rounded-2xl`, { borderWidth: 1, borderColor: '#E8E6EF', shadowColor: '#1D1A23', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.08, shadowRadius: 12, elevation: 4 }]}>
      {/* Premium Badge with Icon */}
      <View style={tw`flex-row items-center justify-between mb-4`}>
        <View style={tw`flex-row items-center`}>
          <View style={[tw`px-4 py-2 rounded-full`, { backgroundColor: '#4F46E5' }]}>
            <Text style={tw`text-white text-sm font-bold`}>{gateText('PREMIUM')}</Text>
          </View>
          {isPreviewMode && (
            <View style={tw`bg-blue-100 px-3 py-1 rounded-full ml-2`}>
              <Text style={tw`text-blue-700 text-xs font-medium`}>{gateText('Preview')}</Text>
            </View>
          )}
        </View>
        <View style={[tw`rounded-full p-2`, { backgroundColor: '#EEF2FF' }]}>
          <Ionicons name="sparkles-outline" size={22} color="#4F46E5" />
        </View>
      </View>

      {/* Content */}
      <Text style={[tw`text-xl font-bold mb-3 leading-6`, { color: '#1D1A23' }]}>
        {customMessage || gateText('Unlock Premium Feature')}
      </Text>

      <Text style={[tw`mb-4 leading-5`, { color: '#777587' }]}>
        {featureDescription}
      </Text>

      {/* Feature Benefits for Legacy Hosted Events */}
      {(feature === PremiumFeature.LIVE_EVENT_CREATE || feature === 'live_events_host') && (
        <View style={tw`mb-5`}>
          <Text style={[tw`text-sm font-medium mb-3`, { color: '#1D1A23' }]}>{gateText("What's included:")}</Text>
          <View style={tw`gap-2`}>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>Access is preserved for existing legacy group subscribers during migration</Text>
            </View>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>New church and large-group hosting now lives in the separate quiz experience</Text>
            </View>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>Your billing continuity remains intact while migration paths are finalized</Text>
            </View>
          </View>
        </View>
      )}

      {/* Feature Benefits for Group Creation */}
      {feature === PremiumFeature.TEAM_MANAGEMENT && (
        <View style={tw`mb-5`}>
          <Text style={[tw`text-sm font-medium mb-3`, { color: '#1D1A23' }]}>{gateText("What you'll get:")}</Text>
          <View style={tw`gap-2`}>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>{uiText('premiumFeatureGroups')}</Text>
            </View>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>{uiText('premiumFeatureHost')}</Text>
            </View>
            <View style={tw`flex-row items-center`}>
              <Ionicons name="checkmark-circle" size={18} color="#10B981" style={tw`mr-2`} />
              <Text style={[tw`text-sm flex-1`, { color: '#464555' }]}>{uiText('premiumFeatureTrack')}</Text>
            </View>
          </View>
        </View>
      )}

      {/* Recommended Plan with Enhanced Styling */}
      {recommendedPlan && (
        <View style={[tw`p-4 rounded-xl mb-5`, { backgroundColor: '#F3F1FA', borderWidth: 1, borderColor: '#E8E6EF' }]}>
          <Text style={[tw`text-sm font-medium mb-2`, { color: '#464555' }]}>
            Available in {recommendedPlan.name}
          </Text>
          <View style={tw`flex-row items-baseline`}>
            <Text style={[tw`text-3xl font-bold`, { color: '#4F46E5' }]}>
              ${recommendedPlan.price.monthly}
            </Text>
            <Text style={[tw`text-base ml-1`, { color: '#777587' }]}>/month</Text>
          </View>
          <Text style={[tw`text-sm font-medium mt-1`, { color: '#10B981' }]}>
            or ${recommendedPlan.price.annual}/year (save {Math.round(((recommendedPlan.price.monthly * 12 - recommendedPlan.price.annual) / (recommendedPlan.price.monthly * 12)) * 100)}%)
          </Text>
        </View>
      )}

      {/* Action Buttons */}
      <View style={tw`flex-row gap-3`}>
        <TouchableOpacity
          style={[tw`flex-1 py-4 rounded-xl shadow-md`, { backgroundColor: '#4F46E5' }]}
          onPress={handleUpgradePress}
        >
          <Text style={tw`text-white font-bold text-center text-base`}>
            {upgradeButtonText || gateText(isPreviewMode ? '✨ Preview Plans' : 'Explore Premium')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={tw`px-4 py-4 border border-gray-300 rounded-xl bg-gray-50`}
          onPress={handleLearnMore}
        >
          <Ionicons name="information-circle-outline" size={22} color="#6b7280" />
        </TouchableOpacity>
      </View>

      {/* Dismiss option */}
      <TouchableOpacity
        style={tw`mt-3 py-2`}
        onPress={() => setDismissed(true)}
      >
        <Text style={tw`text-gray-400 text-center text-sm`}>{uiText('notNow')}</Text>
      </TouchableOpacity>

      {/* Fallback Content */}
      {fallback && (
        <View style={tw`mt-4 pt-4 border-t border-blue-200`}>
          {fallback}
        </View>
      )}

      {/* Feature Details Modal */}
      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
      <FeatureDetailsModal
          feature={feature}
          isPreviewMode={isPreviewMode}
          upgradeButtonText={upgradeButtonText}
          onClose={() => setShowModal(false)}
          onUpgrade={() => {
            setShowModal(false);
            if (!isPreviewMode) {
              navigation.navigate('SubscriptionPlans', {
                preselectedPlan: recommendedPlan?.id
              });
            }
          }}
        />
      </Modal>
    </View>
  );
};

// Feature Details Modal Component
interface FeatureDetailsModalProps {
  feature: PremiumFeature | string;
  isPreviewMode: boolean;
  upgradeButtonText?: string;
  onClose: () => void;
  onUpgrade: () => void;
}

const FeatureDetailsModal: React.FC<FeatureDetailsModalProps> = ({
  feature,
  isPreviewMode,
  upgradeButtonText,
  onClose,
  onUpgrade
}) => {
  // Handle database feature keys that aren't in TypeScript enum
  const getFeatureDescription = (featureKey: string) => {
    if (featureKey === 'live_events_host') {
      return 'Live hosted events now belong to the separate church and large-group experience. Existing legacy group subscribers keep access during migration.';
    }
    // Only use FEATURE_DESCRIPTIONS for actual PremiumFeature enum values
    if (Object.values(PremiumFeature).includes(featureKey as PremiumFeature)) {
      return FEATURE_DESCRIPTIONS[featureKey as PremiumFeature];
    }
    return featureKey;
  };

  const featureDescription = gateText(getFeatureDescription(feature));
  const allPlans = getSubscriptionPlans();

  // Handle database feature keys for plan filtering
  const getPlansWithFeature = (featureKey: string) => {
    if (featureKey === 'live_events_host') {
      return [];
    }
    // Only filter by PremiumFeature enum values
    if (Object.values(PremiumFeature).includes(featureKey as PremiumFeature)) {
      return allPlans.filter(plan => plan.features.includes(featureKey as PremiumFeature));
    }
    return [];
  };

  const plansWithFeature = getPlansWithFeature(feature);

  return (
    <View style={tw`flex-1 bg-white`}>
      {/* Header - Fixed */}
      <View style={tw`flex-row items-center justify-between p-4 pb-3 border-b border-gray-200 bg-white z-10`}>
        <Text style={tw`text-lg font-bold text-gray-800 flex-1`}>
          {gateText(getFeatureDisplayName(feature))}
        </Text>
        <TouchableOpacity
          onPress={onClose}
          style={tw`p-1 rounded-full`}
        >
          <Ionicons name="close" size={22} color="#6b7280" />
        </TouchableOpacity>
      </View>

      {/* Scrollable Content */}
      <ScrollView
        style={tw`flex-1`}
        contentContainerStyle={tw`p-4 pb-6`}
        showsVerticalScrollIndicator={true}
        bounces={true}
      >
        {/* Feature Info */}
        <View style={tw`mb-5`}>
          <View style={[tw`px-3 py-1.5 rounded-full self-start mb-3`, { backgroundColor: '#4F46E5' }]}>
            <Text style={tw`text-white text-xs font-bold`}>{gateText('✨ PREMIUM')}</Text>
          </View>

          <Text style={tw`text-xl font-bold text-gray-800 mb-2 leading-6`}>
            {feature === 'live_events_host'
              ? 'Legacy hosted event access'
              : gateText('Unlock premium play for you and your household')
            }
          </Text>

          <Text style={tw`text-gray-600 text-base leading-5`}>
            {featureDescription}
          </Text>
        </View>

        {/* Benefits */}
        <View style={tw`mb-5`}>
          <Text style={tw`text-lg font-semibold text-gray-800 mb-3`}>{gateText('Benefits')}</Text>
          <View style={tw`gap-2.5`}>
            {getFeatureBenefits(feature).map((benefit, index) => (
              <View key={index} style={tw`flex-row items-start`}>
                <View style={tw`w-5 h-5 rounded-full bg-green-500 items-center justify-center mt-0.5 mr-3`}>
                  <Ionicons name="checkmark" size={12} color="white" />
                </View>
                <Text style={tw`text-gray-700 flex-1 text-sm leading-5`}>{gateText(benefit)}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Available Plans */}
        <View style={tw`mb-5`}>
          <Text style={tw`text-lg font-semibold text-gray-800 mb-3`}>{uiText('availableIn')}</Text>
          {plansWithFeature.map((plan) => (
            <View key={plan.id} style={tw`bg-gray-50 p-3.5 rounded-xl mb-2.5 border border-gray-100`}>
              <View style={tw`flex-row justify-between items-start`}>
                <View style={tw`flex-1 mr-3`}>
                  <Text style={tw`font-semibold text-gray-800 mb-1`}>{plan.name}</Text>
                  <Text style={tw`text-gray-600 text-sm leading-4`}>{plan.description}</Text>
                </View>
                <View style={tw`items-end`}>
                  <Text style={tw`text-blue-600 font-bold text-base`}>
                    ${plan.price.monthly}
                  </Text>
                  <Text style={tw`text-gray-500 text-xs`}>/month</Text>
                </View>
              </View>
            </View>
          ))}
        </View>

        {isPreviewMode && (
          <View style={tw`bg-blue-50 p-4 rounded-xl mb-5 border border-blue-100`}>
            <View style={tw`flex-row items-start`}>
              <Ionicons name="information-circle" size={18} color="#3b82f6" style={tw`mt-0.5 mr-2`} />
              <Text style={tw`text-blue-800 text-sm leading-5 flex-1`}>
                This is a preview of premium features. In live mode, you would be able to subscribe to access this feature.
              </Text>
            </View>
          </View>
        )}

        {/* Bottom spacing for fixed footer */}
        <View style={tw`h-20`} />
      </ScrollView>

      {/* Footer - Fixed */}
      <View style={tw`p-4 pt-3 border-t border-gray-200 bg-white`}>
        <TouchableOpacity
          style={[tw`py-3.5 rounded-xl shadow-sm mb-2.5`, { backgroundColor: '#4F46E5' }]}
          onPress={onUpgrade}
        >
          <Text style={tw`text-white font-semibold text-center text-base`}>
            {upgradeButtonText || gateText(isPreviewMode ? '✨ View All Plans' : 'Explore Premium')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={tw`py-2.5`}
          onPress={onClose}
        >
          <Text style={tw`text-gray-500 text-center text-sm`}>{uiText('maybeLater')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Helper Functions
const getFeatureDisplayName = (feature: PremiumFeature | string): string => {
  // Handle database feature keys first
  if (feature === 'live_events_host') {
    return 'Legacy Hosted Event Access';
  }

  const displayNames: Partial<Record<PremiumFeature, string>> = {
    [PremiumFeature.UNLIMITED_DAILY_QUIZZES]: 'Unlimited Daily Quizzes',
    [PremiumFeature.AD_FREE_EXPERIENCE]: 'Ad-Free Experience',
    [PremiumFeature.UNLIMITED_QUIZ_HISTORY]: 'Unlimited Quiz History',
    [PremiumFeature.MULTIPLE_QUESTION_FORMATS]: 'Multiple Question Formats',
    [PremiumFeature.MULTI_LANGUAGE_SUPPORT]: 'Multi-Language Support',
    [PremiumFeature.MULTIPLAYER_CREATE]: 'Create Multiplayer Rooms',
    [PremiumFeature.MULTIPLAYER_JOIN]: 'Join Multiplayer Rooms',
    [PremiumFeature.GAME_ROOM_CREATE]: 'Create Game Rooms',
    [PremiumFeature.GAME_ROOM_JOIN]: 'Join Game Rooms',
    [PremiumFeature.LIVE_EVENT_CREATE]: 'Legacy Hosted Event Access',
    [PremiumFeature.LIVE_EVENT_JOIN]: 'Legacy Hosted Event Entry',
    [PremiumFeature.FAMILY_ANALYTICS]: 'Family Analytics',
    [PremiumFeature.GROUP_BRANDING]: 'Legacy Group Branding',
    [PremiumFeature.GROUP_ANALYTICS]: 'Legacy Group Analytics',
    [PremiumFeature.GROUP_MEMBER_MANAGEMENT]: 'Member Management',
    [PremiumFeature.GROUP_EVENT_HOSTING]: 'Legacy Hosted Event Tools',
    [PremiumFeature.TEAM_MANAGEMENT]: 'Household Sharing',
    [PremiumFeature.PRIORITY_SUPPORT]: 'Priority Support',
    [PremiumFeature.ADVANCED_ANALYTICS]: 'Advanced Analytics',
    [PremiumFeature.PREMIUM_QUIZ_PACKS]: 'Premium Quiz Packs',
    [PremiumFeature.PREMIUM_LEADER_RESOURCES]: 'Premium Leader Resources',
    [PremiumFeature.SOCIAL_MANUAL_SHARE]: 'Manual Social Sharing',
    [PremiumFeature.SOCIAL_SCHEDULER_BASIC]: 'Basic Social Scheduler',
    [PremiumFeature.SOCIAL_SCHEDULER_PRO]: 'Pro Social Scheduler',
    [PremiumFeature.SOCIAL_UNLIMITED]: 'Unlimited Social Scheduler',
    [PremiumFeature.SOCIAL_AI_FACTORY]: 'AI Social Factory',
    [PremiumFeature.SOCIAL_ENTERPRISE]: 'Enterprise Social Suite',
  };

  // Only use displayNames for actual PremiumFeature enum values
  if (Object.values(PremiumFeature).includes(feature as PremiumFeature)) {
    return displayNames[feature as PremiumFeature] ?? feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  return feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getFeatureBenefits = (feature: PremiumFeature | string): string[] => {
  // Handle database feature keys first
  if (feature === 'live_events_host') {
    return [
      'Lives in the separate church and large-group app',
      'No longer sold inside the consumer Bible quiz app',
      'Keeps the consumer plan focused on solo and household play'
    ];
  }

  const benefits: Partial<Record<PremiumFeature, string[]>> = {
    [PremiumFeature.UNLIMITED_DAILY_QUIZZES]: [
      'No daily quiz limits',
      'Play as many quizzes as you want',
      'Access to all categories'
    ],
    [PremiumFeature.AD_FREE_EXPERIENCE]: [
      'Uninterrupted quiz gameplay',
      'Faster loading times',
      'Cleaner, more focused interface'
    ],
    [PremiumFeature.UNLIMITED_QUIZ_HISTORY]: [
      'Access all your past quiz results',
      'Track your progress over time',
      'Review questions you got wrong'
    ],
    [PremiumFeature.MULTIPLE_QUESTION_FORMATS]: [
      '5 different question types',
      'Multiple choice, true/false, fill blanks',
      'Ordering and reordering questions'
    ],
    [PremiumFeature.MULTI_LANGUAGE_SUPPORT]: [
      'Quiz in 10 different languages',
      'More languages coming soon',
      'Study in your preferred language'
    ],
    [PremiumFeature.MULTIPLAYER_CREATE]: [
      'Create quiz rooms for friends',
      'Host multiplayer Bible sessions',
      'Set custom room settings'
    ],
    [PremiumFeature.MULTIPLAYER_JOIN]: [
      'Join any multiplayer room',
      'Participate in group Bible study',
      'Connect with other players'
    ],
    [PremiumFeature.GAME_ROOM_CREATE]: [
      'Create advanced game rooms',
      'Support 6+ players',
      'Advanced features and analytics'
    ],
    [PremiumFeature.GAME_ROOM_JOIN]: [
      'Join family and group game rooms',
      'Participate in larger sessions',
      'Access advanced game features'
    ],
    [PremiumFeature.LIVE_EVENT_CREATE]: [
      'Preserved for existing legacy group subscribers',
      'Supports continuity while hosted-event migration is completed',
      'Not sold as a new consumer-app feature'
    ],
    [PremiumFeature.LIVE_EVENT_JOIN]: [
      'Live event access now belongs to the separate church and large-group app',
      'Consumer Premium focuses on personal and household play'
    ],
    [PremiumFeature.GROUP_BRANDING]: [
      'Preserved for legacy group subscribers',
      'Supports historical group customization needs',
      'Kept for continuity rather than new consumer sales'
    ],
    [PremiumFeature.GROUP_ANALYTICS]: [
      'Preserved legacy reporting access',
      'Review historical participation patterns',
      'Retained only for existing group continuity'
    ],
    [PremiumFeature.FAMILY_ANALYTICS]: [
      'Track family member progress',
      'Monitor learning achievements',
      'Generate family reports'
    ],
    [PremiumFeature.GROUP_MEMBER_MANAGEMENT]: [
      'Manage legacy group participant records',
      'Review participant progress history',
      'Retained while migration paths are completed'
    ],
    [PremiumFeature.GROUP_EVENT_HOSTING]: [
      'Preserved hosted-event tools for legacy group subscribers',
      'Allows existing operations to continue during migration',
      'Not marketed as a new consumer-app capability'
    ],
    [PremiumFeature.TEAM_MANAGEMENT]: [
      'Invite up to 5 family members under one Premium subscription',
      'Manage your household in one private family hub',
      'Keep everyone on the same Premium plan'
    ],
    [PremiumFeature.PRIORITY_SUPPORT]: [
      'Priority customer support',
      'Faster response times',
      'Dedicated support channel'
    ],
    [PremiumFeature.ADVANCED_ANALYTICS]: [
      'Detailed performance analytics',
      'Advanced reporting tools',
      'Custom data insights'
    ],
    [PremiumFeature.PREMIUM_QUIZ_PACKS]: [
      'Exclusive quiz content',
      'Premium question sets',
      'Expert-crafted questions'
    ],
    [PremiumFeature.PREMIUM_LEADER_RESOURCES]: [
      'Exclusive leader materials',
      'Teaching resources',
      'Leadership development tools'
    ],
    [PremiumFeature.SOCIAL_MANUAL_SHARE]: [
      'Manually share quiz content to major social platforms',
      'Use branded share templates',
      'Invite new players with less friction'
    ],
    [PremiumFeature.SOCIAL_SCHEDULER_BASIC]: [
      'Schedule a limited number of social posts each month',
      'Reach more people across multiple platforms',
      'Use premium scheduling templates'
    ],
    [PremiumFeature.SOCIAL_SCHEDULER_PRO]: [
      'Higher scheduling limits for growing families and ministries',
      'Support more social platforms',
      'Unlock richer campaign templates'
    ],
    [PremiumFeature.SOCIAL_UNLIMITED]: [
      'Unlimited scheduled social sharing',
      'Support larger collaboration teams',
      'Scale outreach without post limits'
    ],
    [PremiumFeature.SOCIAL_AI_FACTORY]: [
      'Generate AI-assisted social content',
      'Produce more platform-ready campaign variations',
      'Support larger outreach teams'
    ],
    [PremiumFeature.SOCIAL_ENTERPRISE]: [
      'Enterprise-grade social distribution features',
      'Unlimited scale for teams and platforms',
      'Access advanced integration capabilities'
    ]
  };
  
  // Only use benefits for actual PremiumFeature enum values
  if (Object.values(PremiumFeature).includes(feature as PremiumFeature)) {
    return benefits[feature as PremiumFeature] ?? [
      'Enhanced functionality',
      'Professional features',
      'Priority access'
    ];
  }

  return [
    'Enhanced functionality',
    'Professional features',
    'Priority access'
  ];
};

// Hook for easy feature access checking
export const usePremiumFeature = (feature: PremiumFeature) => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const checkAccess = async () => {
      if (!user?.id) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      try {
        const access = await entitlementService.hasFeatureAccess(user.id, feature);
        setHasAccess(access);
      } catch (error) {
        console.error('Failed to check feature access:', error);
        setHasAccess(true); // Default to allowing access on error
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, feature]);

  return { hasAccess, loading };
};

export default PremiumFeatureGate;
